import { useState, useEffect } from 'react'
import { apiClient } from '../services/api'
import { useAuditorStore } from '../stores/auditorStore'
import { Calendar, TrendingUp, ShoppingCart, CreditCard, Receipt, Download, Filter } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import toast from 'react-hot-toast'

type PeriodType = 'day' | 'week' | 'month' | 'year' | 'custom'

interface ReportData {
  summary: {
    total_sales: number
    total_orders: number
    total_tax: number
    average_order_value: number
  }
  products_sold: Array<{
    product_id: string
    product_name: string
    sku: string
    quantity_sold: number
    total_revenue: number
  }>
  payment_methods: Array<{
    payment_method: string
    count: number
    total_amount: number
  }>
  daily_sales?: Array<{
    date: string
    total_sales: number
    order_count: number
  }>
}

export const Reports = () => {
  const { isAuditor, currentBusiness } = useAuditorStore()
  const [periodType, setPeriodType] = useState<PeriodType>('day')
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0])
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7))
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString())
  const [customStartDate, setCustomStartDate] = useState(new Date().toISOString().split('T')[0])
  const [customEndDate, setCustomEndDate] = useState(new Date().toISOString().split('T')[0])

  const [reportData, setReportData] = useState<ReportData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // For auditors, only load if a business is selected
    if (isAuditor && !currentBusiness) {
      setLoading(false)
      return
    }

    loadReport()
  }, [periodType, selectedDate, selectedMonth, selectedYear, customStartDate, customEndDate, isAuditor, currentBusiness?.merchant_id])

  const getDateRange = () => {
    const today = new Date()
    let startDate = ''
    let endDate = ''

    switch (periodType) {
      case 'day':
        startDate = selectedDate
        endDate = selectedDate
        break

      case 'week':
        const weekDate = new Date(selectedDate)
        const dayOfWeek = weekDate.getDay()
        const startOfWeek = new Date(weekDate)
        startOfWeek.setDate(weekDate.getDate() - dayOfWeek)
        const endOfWeek = new Date(startOfWeek)
        endOfWeek.setDate(startOfWeek.getDate() + 6)
        startDate = startOfWeek.toISOString().split('T')[0]
        endDate = endOfWeek.toISOString().split('T')[0]
        break

      case 'month':
        const [year, month] = selectedMonth.split('-')
        const firstDay = new Date(parseInt(year), parseInt(month) - 1, 1)
        const lastDay = new Date(parseInt(year), parseInt(month), 0)
        startDate = firstDay.toISOString().split('T')[0]
        endDate = lastDay.toISOString().split('T')[0]
        break

      case 'year':
        startDate = `${selectedYear}-01-01`
        endDate = `${selectedYear}-12-31`
        break

      case 'custom':
        startDate = customStartDate
        endDate = customEndDate
        break
    }

    return { startDate, endDate }
  }

  const loadReport = async () => {
    try {
      setLoading(true)
      setError(null)

      const { startDate, endDate } = getDateRange()

      const response = await apiClient.getSalesReport(startDate, endDate)

      setReportData(response.data || response)
    } catch (err: any) {
      console.error('Failed to load report:', err)
      setError(err.message || 'Failed to load report')
      toast.error('Failed to load report')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const handleDownloadReport = () => {
    toast.info('Download feature coming soon!')
  }

  const getPeriodLabel = () => {
    const { startDate, endDate } = getDateRange()
    if (startDate === endDate) {
      return new Date(startDate).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
    return `${new Date(startDate).toLocaleDateString('en-IN', {
      month: 'short',
      day: 'numeric'
    })} - ${new Date(endDate).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })}`
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Sales Reports</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Analyze your sales performance and trends</p>
          </div>
          <Button onClick={handleDownloadReport} variant="outline" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Download PDF
          </Button>
        </div>
      </div>

      {/* Period Selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Select Period
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Period Type Buttons */}
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'day', label: 'Day' },
                { value: 'week', label: 'Week' },
                { value: 'month', label: 'Month' },
                { value: 'year', label: 'Year' },
                { value: 'custom', label: 'Custom Range' }
              ].map((period) => (
                <Button
                  key={period.value}
                  variant={periodType === period.value ? 'default' : 'outline'}
                  onClick={() => setPeriodType(period.value as PeriodType)}
                  className={periodType === period.value ? 'bg-orange-600 hover:bg-orange-700' : ''}
                >
                  {period.label}
                </Button>
              ))}
            </div>

            {/* Date/Period Selectors */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {periodType === 'day' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Date
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              )}

              {periodType === 'week' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Week (any day in the week)
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              )}

              {periodType === 'month' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Month
                  </label>
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              )}

              {periodType === 'year' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Year
                  </label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              )}

              {periodType === 'custom' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Showing data for: <span className="font-semibold text-gray-900 dark:text-gray-100">{getPeriodLabel()}</span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Report Data */}
      {!loading && reportData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Sales</span>
                </div>
                <div className="text-3xl font-bold text-blue-900 dark:text-blue-100">
                  {formatCurrency(reportData.summary?.total_sales || 0)}
                </div>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  {reportData.summary?.total_orders || 0} orders
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <ShoppingCart className="w-8 h-8 text-green-600 dark:text-green-400" />
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">Avg Order Value</span>
                </div>
                <div className="text-3xl font-bold text-green-900 dark:text-green-100">
                  {formatCurrency(reportData.summary?.average_order_value || 0)}
                </div>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  Per transaction
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <Receipt className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                  <span className="text-sm font-medium text-purple-600 dark:text-purple-400">Tax Collected</span>
                </div>
                <div className="text-3xl font-bold text-purple-900 dark:text-purple-100">
                  {formatCurrency(reportData.summary?.total_tax || 0)}
                </div>
                <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                  GST/VAT
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-orange-200 dark:border-orange-700">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <CreditCard className="w-8 h-8 text-orange-600 dark:text-orange-400" />
                  <span className="text-sm font-medium text-orange-600 dark:text-orange-400">Total Orders</span>
                </div>
                <div className="text-3xl font-bold text-orange-900 dark:text-orange-100">
                  {reportData.summary?.total_orders || 0}
                </div>
                <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                  Completed
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Payment Methods */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Payment Methods
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Payment Method
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Transactions
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Total Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Percentage
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {(reportData.payment_methods || []).map((method, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 capitalize">
                            {method.payment_method}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {method.count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {formatCurrency(parseFloat(method.total_amount?.toString() || '0'))}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {((parseFloat(method.total_amount?.toString() || '0') / (reportData.summary?.total_sales || 1)) * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!reportData.payment_methods || reportData.payment_methods.length === 0) && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No payment data available for this period
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Selling Products */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Top Selling Products
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        SKU
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Quantity Sold
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Revenue
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {(reportData.products_sold || []).map((product, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {product.product_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {product.sku || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                            {parseFloat(product.quantity_sold?.toString() || '0')} units
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {formatCurrency(parseFloat(product.total_revenue?.toString() || '0'))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!reportData.products_sold || reportData.products_sold.length === 0) && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No product sales data available for this period
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
